import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import type { GenerationProgress, ProgressEventType } from '@/types';

interface UseProgressStreamOptions {
  /** History ID to stream progress for */
  historyId: string | null;
  /** Whether to auto-connect when historyId is provided */
  autoConnect?: boolean;
  /** Callback when generation completes */
  onComplete?: (historyId: string) => void;
  /** Callback when generation fails */
  onFailed?: (error: string) => void;
}

/** Shorthand options when using the two-argument form */
interface ShorthandOptions {
  /** Whether the stream is enabled */
  enabled?: boolean;
  /** Callback when generation completes */
  onComplete?: () => void;
  /** Callback on error */
  onError?: (error: string) => void;
}

interface UseProgressStreamReturn {
  /** Current progress state */
  progress: GenerationProgress | null;
  /** Whether the stream is connected */
  isConnected: boolean;
  /** Whether we're currently connecting */
  isConnecting: boolean;
  /** Any error that occurred */
  error: string | null;
  /** Manually connect to the stream */
  connect: () => void;
  /** Disconnect from the stream */
  disconnect: () => void;
  /** Last event type received */
  lastEventType: ProgressEventType | null;
}

/**
 * Hook to stream real-time progress updates via Server-Sent Events.
 *
 * Can be called with:
 * - Single options object: useProgressStream({ historyId, autoConnect, ... })
 * - Two arguments: useProgressStream(historyId, { enabled, onComplete, onError })
 */
export function useProgressStream(
  optionsOrHistoryId: UseProgressStreamOptions | string | null,
  shorthandOptions?: ShorthandOptions
): UseProgressStreamReturn {
  // Parse arguments - support both calling conventions
  let historyId: string | null;
  let autoConnect: boolean;
  let onComplete: ((historyId: string) => void) | undefined;
  let onFailed: ((error: string) => void) | undefined;

  if (typeof optionsOrHistoryId === 'string' || optionsOrHistoryId === null) {
    // Two-argument form: useProgressStream(historyId, { enabled, ... })
    historyId = optionsOrHistoryId;
    autoConnect = shorthandOptions?.enabled ?? true;
    onComplete = shorthandOptions?.onComplete ? () => shorthandOptions.onComplete!() : undefined;
    onFailed = shorthandOptions?.onError;
  } else {
    // Options object form: useProgressStream({ historyId, ... })
    historyId = optionsOrHistoryId.historyId;
    autoConnect = optionsOrHistoryId.autoConnect ?? true;
    onComplete = optionsOrHistoryId.onComplete;
    onFailed = optionsOrHistoryId.onFailed;
  }

  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastEventType, setLastEventType] = useState<ProgressEventType | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onFailedRef = useRef(onFailed);
  onCompleteRef.current = onComplete;
  onFailedRef.current = onFailed;

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  }, []);

  const connect = useCallback(() => {
    if (!historyId) return;
    if (eventSourceRef.current) {
      disconnect();
    }

    setIsConnecting(true);
    setError(null);

    // SSE endpoint URL
    const url = `${API_BASE_URL}/api/v1/newsletter/progress/${historyId}`;

    // Create EventSource with credentials
    const eventSource = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
      setIsConnecting(false);
    };

    eventSource.onerror = () => {
      setError('Connection lost. Reconnecting...');
      setIsConnected(false);
      // EventSource will auto-reconnect
    };

    // Handle progress events
    eventSource.addEventListener('progress', (event) => {
      try {
        const data = JSON.parse(event.data) as GenerationProgress;
        setProgress(data);
        setLastEventType('progress');
        setError(null);
      } catch (e) {
        console.error('Failed to parse progress event:', e);
      }
    });

    // Handle completion
    eventSource.addEventListener('complete', (event) => {
      setLastEventType('complete');
      try {
        const data = JSON.parse(event.data);
        onCompleteRef.current?.(data.history_id || historyId);
      } catch (e) {
        onCompleteRef.current?.(historyId);
      }
      disconnect();
    });

    // Handle failure
    eventSource.addEventListener('failed', (event) => {
      setLastEventType('failed');
      try {
        const data = JSON.parse(event.data);
        setError(data.error || 'Generation failed');
        onFailedRef.current?.(data.error || 'Generation failed');
      } catch (e) {
        onFailedRef.current?.('Generation failed');
      }
      disconnect();
    });

    // Handle heartbeat (keep-alive)
    eventSource.addEventListener('heartbeat', () => {
      setLastEventType('heartbeat');
    });

    // Handle timeout
    eventSource.addEventListener('timeout', () => {
      setLastEventType('timeout');
      setError('Progress stream timeout');
      disconnect();
    });

    // Handle error events
    eventSource.addEventListener('error', (event) => {
      setLastEventType('error');
      try {
        const data = JSON.parse((event as MessageEvent).data);
        setError(data.error || 'Stream error');
      } catch {
        // Not a data error, connection error handled by onerror
      }
    });
  }, [historyId, disconnect]);

  // Auto-connect when historyId changes
  useEffect(() => {
    if (autoConnect && historyId) {
      connect();
    }
    return () => {
      disconnect();
    };
  }, [historyId, autoConnect, connect, disconnect]);

  return {
    progress,
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
    lastEventType,
  };
}

